import React, { useMemo, useState } from 'react';
import {
  PdsBox,
  PdsMantineText,
  PdsMantineButton,
} from '@podium-design-system/react-components';
import Pagination from '../Pagination';
import InvestigatePairButtons from '../InvestigatePairButtons';
import { formatSeenDate, formatDurationSeen } from '../../utils/dateDisplay';
import {
  HISTORICAL_DNS_RECORD_TYPES,
  filterHistoricalDnsByType,
  historicalDnsFirstColumnHeader,
  historicalDnsDisplayValue,
  historicalDnsInvestigateTarget,
  historicalDnsInvestigateMeta,
  countHistoricalDnsByType,
} from '../../utils/historicalDnsDisplay';

const PAGE_SIZE = 25;

const SecurityTrailsHistoricalPanel = ({
  details,
  domain,
  keywordFilteredRecords,
  onRunTools,
  onAddNodeOnly,
  showInvestigationPair = false,
  isTargetAlreadyOnCaseMap,
}) => {
  const [recordTypeFilter, setRecordTypeFilter] = useState('A');
  const [page, setPage] = useState(1);

  const typeCounts = useMemo(
    () => countHistoricalDnsByType(keywordFilteredRecords),
    [keywordFilteredRecords],
  );

  const filteredByType = useMemo(
    () => filterHistoricalDnsByType(keywordFilteredRecords, recordTypeFilter),
    [keywordFilteredRecords, recordTypeFilter],
  );

  const totalPages = Math.max(1, Math.ceil(filteredByType.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedRows = filteredByType.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const firstColumnHeader = historicalDnsFirstColumnHeader(recordTypeFilter);
  const headingDomain = domain || details?.targetValue || 'domain';
  const showActions = Boolean(showInvestigationPair && onRunTools);
  const actionColSpan = showActions ? 1 : 0;
  const dataColCount = 5 + actionColSpan;

  return (
    <PdsBox direction="column" gap="md">
      <PdsMantineText type="subtitle">
        {headingDomain} historical {recordTypeFilter} data
      </PdsMantineText>

      <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center">
        {HISTORICAL_DNS_RECORD_TYPES.map((type) => (
          <PdsMantineButton
            key={type}
            rank={recordTypeFilter === type ? 'fill' : 'outline'}
            color={recordTypeFilter === type ? 'action' : 'neutral'}
            size="xs"
            onClick={() => {
              setRecordTypeFilter(type);
              setPage(1);
            }}
          >
            {type} ({typeCounts[type] || 0})
          </PdsMantineButton>
        ))}
      </PdsBox>

      <div style={{ overflowX: 'auto', border: '1px solid #d0d7de', borderRadius: 8 }}>
        <table className="argus-table-investigation" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', textAlign: 'left' }}>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>{firstColumnHeader}</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de' }}>Organization</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>First Seen</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>Last Seen</th>
              <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>Duration Seen</th>
              {showActions && (
                <th style={{ padding: '8px 12px', borderBottom: '1px solid #d0d7de', width: 100 }}> </th>
              )}
            </tr>
          </thead>
          <tbody>
            {paginatedRows.length === 0 ? (
              <tr>
                <td colSpan={dataColCount} style={{ padding: 12 }}>
                  No historical {recordTypeFilter} records.
                </td>
              </tr>
            ) : (
              paginatedRows.map((row, idx) => {
                const displayValue = historicalDnsDisplayValue(row);
                const investigateTarget = historicalDnsInvestigateTarget(row);
                const { deriveFrom, connectionLabel } = historicalDnsInvestigateMeta(row);
                const rowKey = `${row.recordType}-${row.recordValue}-${row.firstSeen}-${row.lastSeen}-${idx}`;
                return (
                  <tr key={rowKey} style={{ verticalAlign: 'top' }}>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', wordBreak: recordTypeFilter === 'TXT' ? 'break-word' : 'normal' }}>
                      <span className="argus-data">{displayValue}</span>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                      {row.organization || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', whiteSpace: 'nowrap' }} className="argus-type-row-meta">
                      {formatSeenDate(row.firstSeen)}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', whiteSpace: 'nowrap' }} className="argus-type-row-meta">
                      {row.lastSeen ? formatSeenDate(row.lastSeen) : formatSeenDate(new Date())}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2', whiteSpace: 'nowrap' }} className="argus-type-row-meta">
                      {formatDurationSeen(row.firstSeen, row.lastSeen)}
                    </td>
                    {showActions && investigateTarget && (
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }}>
                        <InvestigatePairButtons
                          targetDomain={investigateTarget}
                          deriveFrom={deriveFrom}
                          connectionLabel={connectionLabel}
                          onRunTools={onRunTools}
                          onAddNodeOnly={onAddNodeOnly}
                          showAddNode={!isTargetAlreadyOnCaseMap?.(investigateTarget)}
                        />
                      </td>
                    )}
                    {showActions && !investigateTarget && (
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eaeef2' }} />
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filteredByType.length > PAGE_SIZE && (
        <Pagination
          currentPage={safePage}
          totalItems={filteredByType.length}
          itemsPerPage={PAGE_SIZE}
          onPageChange={setPage}
        />
      )}
    </PdsBox>
  );
};

export default SecurityTrailsHistoricalPanel;
